import { getQueryParams } from '@zonofi/common/functions/get-query-param';
import { zonoApi } from '../api/zonoApi';
import {
  InvoiceDetailsRequest,
  Invoice,
  InvoiceList,
  InvoiceRequest,
  InvoiceParsedDataRequest,
  InvoiceParsedDataResponse,
  ChannelDetails,
  ChannelDetailsRequest,
  DispatchNoteParsedDataResponse,
  DispatchNoteParsedDataRequest,
  InvoiceListRequest,
  DispatchListRequest,
  DispatchListResponse,
  DispatchListDetailsRes,
  createDispatchNoteReq,
  createDispatchNoteResponse,
} from './invoice-model';

export const invoiceApi = zonoApi.injectEndpoints({
  endpoints: (builder) => ({
    getInvoiceDetails: builder.query<Invoice, InvoiceDetailsRequest>({
      query(arg) {
        const { invoiceId, ...params } = arg;
        return {
          url: `invoiceservice/invoice/${invoiceId}?includePayment=true`,
          method: 'GET',
          params: params,
        };
      },
    }),
    getInvoiceList: builder.query<InvoiceList, InvoiceListRequest>({
      query(arg) {
        const { workspaceId, ...params } = arg;
        return {
          url: `invoiceservice/invoices/${workspaceId}`,
          method: 'GET',
          params: params,
        };
      },
    }),
    downloadOrderInvoice: builder.mutation<
      string,
      { workspaceId: string; body: any }
    >({
      query(args) {
        return {
          url: `invoiceservice/invoices/download/${args.workspaceId}?sellerWorkspaceId=${args.workspaceId}`,
          method: 'POST',
          body: args.body,
          responseHandler: (response) => response.text(),
        };
      },
    }),
    getAllInvoice: builder.mutation<InvoiceList, InvoiceRequest>({
      query(arg) {
        const { workspaceId, body, ...params } = arg;
        return {
          url: `invoiceservice/invoices/all/${workspaceId}`,
          method: 'POST',
          body: body || {},
          params: params,
        };
      },
    }),
    getInvoiceParsedData: builder.query<
      InvoiceParsedDataResponse,
      InvoiceParsedDataRequest
    >({
      query(params) {
        const { body, ...otherParams } = params;
        return {
          url: `hub/bot/api/v1/messages`,
          method: 'POST',
          params: otherParams,
          body: body,
        };
      },
    }),
    createInvoice: builder.mutation<Invoice, any>({
      query(arg) {
        const { workspaceId, ...body } = arg;
        return {
          url: `invoiceservice/invoice/${workspaceId}`,
          method: 'POST',
          body: body,
        };
      },
    }),
    updateInvoice: builder.mutation<Invoice, any>({
      query(arg) {
        const { workspaceId, ...body } = arg;
        return {
          url: `invoiceservice/invoice/${workspaceId}`,
          method: 'PUT',
          body: body,
        };
      },
    }),
    getChannelDetails: builder.query<ChannelDetails, ChannelDetailsRequest>({
      query: (payload) => {
        const {
          workspaceId,
          customerId,
          channelType,
          channelId: id,
          sellerWorkspaceId,
        } = payload;
        return {
          url: `hub/communication-v2/api/channel/member`,
          method: 'GET',
          params: {
            workspaceId,
            id,
            sellerWorkspaceId,
            customerId,
            channelType,
            random: Math.random(),
          },
        };
      },
    }),
    getDispatchNoteParsedData: builder.query<
      DispatchNoteParsedDataResponse,
      DispatchNoteParsedDataRequest
    >({
      query(params) {
        return {
          url: `invoiceservice/dispatch-parser`,
          method: 'POST',
          params: { workspaceId: params.workspaceId },
          body: params.body,
        };
      },
    }),
    getDispatchNoteList: builder.query<
      DispatchListResponse,
      DispatchListRequest
    >({
      query(args) {
        return {
          url: `/invoiceservice/dispatch/${args.workspaceId}?${getQueryParams(
            args.body || {}
          )}`,
          method: 'GET',
        };
      },
    }),
    getDispatchNoteDetails: builder.query<
      DispatchListDetailsRes,
      { dispatchId: string; workspaceId: string }
    >({
      query(args) {
        return {
          url: `/invoiceservice/dispatch/details/${args.dispatchId}/${args.workspaceId}`,
          method: 'GET',
        };
      },
    }),
    createDispatchNote: builder.mutation<
      createDispatchNoteResponse,
      createDispatchNoteReq
    >({
      query(args) {
        return {
          url: `/invoiceservice/dispatch/${args.workspaceId}`,
          method: 'POST',
          body: args,
        };
      },
    }),
  }),
  overrideExisting: true,
});

export const {
  useLazyGetInvoiceDetailsQuery,
  useLazyGetInvoiceListQuery,
  useGetAllInvoiceMutation,
  useDownloadOrderInvoiceMutation,
  useCreateInvoiceMutation,
  useUpdateInvoiceMutation,
  useLazyGetInvoiceParsedDataQuery,
  useLazyGetChannelDetailsQuery,
  useLazyGetDispatchNoteParsedDataQuery,
  useLazyGetDispatchNoteListQuery,
  useLazyGetDispatchNoteDetailsQuery,
  useCreateDispatchNoteMutation,
} = invoiceApi;
